import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import {
  LayoutDashboard,
  ShoppingCart,
  UtensilsCrossed,
  Package,
  DollarSign,
  Users,
  Settings,
  LogOut,
  ChevronLeft,
  Menu,
  Sparkles,
  Store,
  Bell,
  HelpCircle,
  X,
  Check,
  PhoneCall
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { useOrderNotifications } from '@/hooks/useOrderNotifications';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const LOGO_URL = "/logo-adegga.png";

const navItems = [
  { path: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/app/pedidos', label: 'Pedidos', icon: ShoppingCart },
  { path: '/app/pdv', label: 'Vendas na Porta', icon: Store },
  { path: '/app/venda-online', label: 'Venda Direta Online', icon: PhoneCall },
  { path: '/app/cardapio', label: 'Cardápio', icon: UtensilsCrossed },
  { path: '/app/estoque', label: 'Estoque', icon: Package },
  { path: '/app/financeiro', label: 'Financeiro', icon: DollarSign },
  { path: '/app/clientes', label: 'Clientes', icon: Users },
  { path: '/app/promocoes', label: 'Promoções IA', icon: Sparkles },
  { path: '/app/suporte', label: 'Suporte', icon: HelpCircle },
  { path: '/app/configuracoes', label: 'Configurações', icon: Settings },
];

const Sidebar = ({ mobile, onClose, newOrdersCount }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/app/login');
  };

  return (
    <div className="h-full flex flex-col bg-[#0a0a0f] border-r border-white/5">
      <div className="p-4 border-b border-white/5">
        <img src={LOGO_URL} alt="ADEGGA" className="h-10 object-contain" />
      </div>
      
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
        {navItems.map(item => (
          <Link
            key={item.path}
            to={item.path}
            onClick={onClose}
            className={`sidebar-item ${location.pathname === item.path ? 'active' : ''}`}
            data-testid={`nav-${item.path.split('/').pop()}`}
          >
            <item.icon className="w-5 h-5" />
            <span className="flex-1">{item.label}</span>
            {item.path === '/app/pedidos' && newOrdersCount > 0 && (
              <Badge className="bg-red-500 text-white text-xs px-1.5 py-0.5 ml-auto animate-pulse">
                {newOrdersCount}
              </Badge>
            )}
          </Link>
        ))}
      </nav>
      
      <div className="p-2 border-t border-white/5">
        <button
          onClick={handleLogout}
          className="sidebar-item w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <span>Sair</span>
        </button>
      </div>
    </div>
  );
};

export const DashboardLayout = ({ children, title }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [storeId, setStoreId] = useState(null);
  const [storeConfig, setStoreConfig] = useState(null);
  const [newOrdersCount, setNewOrdersCount] = useState(0);

  // Fetch store ID for WebSocket connection
  useEffect(() => {
    const fetchStoreId = async () => {
      try {
        const res = await axios.get(`${API}/stores/me`);
        setStoreId(res.data.id);
        setStoreConfig(res.data);
      } catch (e) {
        console.error('Error fetching store:', e);
      }
    };
    if (user?.store_id) {
      setStoreId(user.store_id);
      // Still fetch full store data for config
      fetchStoreId();
    } else {
      fetchStoreId();
    }
  }, [user]);

  // Use ref for location to avoid reconnections
  const locationRef = React.useRef(location.pathname);
  
  React.useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  // Handle new order notifications globally - stable callback
  const handleNewOrder = React.useCallback((data) => {
    // Increment counter if not on orders page
    if (locationRef.current !== '/app/pedidos') {
      setNewOrdersCount(prev => prev + 1);
    }
  }, []); // Empty deps - uses ref instead

  // Reset counter when visiting orders page
  useEffect(() => {
    if (location.pathname === '/app/pedidos') {
      setNewOrdersCount(0);
    }
  }, [location.pathname]);

  // Initialize global WebSocket connection for notifications
  useOrderNotifications(
    storeId, 
    handleNewOrder, 
    storeConfig?.print_config?.auto_print_enabled || false,
    storeConfig?.print_config || {},
    storeConfig?.notification_config || { sound_enabled: true, sound_type: 'default', volume: 100 }
  );

  // Admin notifications state
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // Fetch admin notifications
  const fetchAdminNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setAdminNotifications(res.data || []);
    } catch (e) {
      console.error('Error fetching notifications:', e);
    }
  }, []);

  // Fetch notifications on mount and periodically
  useEffect(() => {
    fetchAdminNotifications();
    const interval = setInterval(fetchAdminNotifications, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchAdminNotifications]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`);
      fetchAdminNotifications();
    } catch (e) {
      console.error('Error marking notification as read:', e);
    }
  };

  // Get unread count
  const unreadNotifications = adminNotifications.filter(
    n => !n.read_by?.includes(user?.store_id)
  );

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 60) return `${minutes}min atrás`;
    if (hours < 24) return `${hours}h atrás`;
    if (days < 7) return `${days}d atrás`;
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className="min-h-screen bg-[#1a1a2e] flex">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-64 fixed h-screen">
        <Sidebar newOrdersCount={newOrdersCount} />
      </div>
      
      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64 bg-[#0a0a0f] border-white/5">
          <Sidebar mobile onClose={() => setMobileOpen(false)} newOrdersCount={newOrdersCount} />
        </SheetContent>
      </Sheet>
      
      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-[#1a1a2e]/95 backdrop-blur-md border-b border-white/5 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-white"
                onClick={() => setMobileOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-bold text-white">{title}</h1>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Notification Bell */}
              <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid="notification-bell"
                    className="relative text-white/70 hover:text-white hover:bg-white/10"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadNotifications.length > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] min-w-[18px] h-[18px] rounded-full flex items-center justify-center animate-pulse">
                        {unreadNotifications.length}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent 
                  className="w-80 p-0 bg-[#1a1a2e] border-white/10" 
                  align="end"
                  sideOffset={8}
                >
                  <div className="p-3 border-b border-white/10">
                    <h3 className="font-semibold text-white">Notificações</h3>
                    <p className="text-xs text-white/50">
                      {unreadNotifications.length > 0 
                        ? `${unreadNotifications.length} não lida(s)` 
                        : 'Nenhuma notificação nova'}
                    </p>
                  </div>
                  
                  <div className="max-h-[400px] overflow-y-auto">
                    {adminNotifications.length === 0 ? (
                      <div className="p-6 text-center text-white/50">
                        <Bell className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p>Nenhuma notificação</p>
                      </div>
                    ) : (
                      adminNotifications.map((notification) => {
                        const isUnread = !notification.read_by?.includes(user?.store_id);
                        return (
                          <div
                            key={notification.id}
                            className={`p-3 border-b border-white/5 hover:bg-white/5 transition-colors ${
                              isUnread ? 'bg-[#ff6b00]/5' : ''
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${
                                notification.type === 'warning' ? 'bg-yellow-500' :
                                notification.type === 'error' ? 'bg-red-500' :
                                notification.type === 'success' ? 'bg-green-500' :
                                'bg-blue-500'
                              }`} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm ${isUnread ? 'text-white font-medium' : 'text-white/80'}`}>
                                  {notification.title}
                                </p>
                                <p className="text-xs text-white/50 mt-1 line-clamp-2">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-white/30 mt-1">
                                  {formatDate(notification.created_at)}
                                </p>
                              </div>
                              {isUnread && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="shrink-0 w-7 h-7 text-white/40 hover:text-green-400 hover:bg-green-500/10"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markAsRead(notification.id);
                                  }}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  
                  {adminNotifications.length > 0 && unreadNotifications.length > 0 && (
                    <div className="p-2 border-t border-white/10">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-white/60 hover:text-white text-xs"
                        onClick={() => {
                          unreadNotifications.forEach(n => markAsRead(n.id));
                        }}
                      >
                        Marcar todas como lidas
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>

              <span className="text-sm text-white/60 hidden sm:block">{user?.name}</span>
              <div className="w-8 h-8 rounded-full bg-[#ff6b00] flex items-center justify-center text-white font-bold text-sm">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        </header>
        
        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
      
      {/* Mobile Bottom Nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-black/90 backdrop-blur-md border-t border-white/10 px-2 py-2 z-50">
        <div className="flex justify-around">
          {navItems.slice(0, 5).map(item => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`mobile-nav-item ${isActive ? 'active' : ''} relative`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-[10px]">{item.label}</span>
                {item.path === '/app/pedidos' && newOrdersCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center animate-pulse">
                    {newOrdersCount}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default DashboardLayout;
